import { AgentChat as Chat } from './components/agent-chat';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuid } from '@lukeed/uuid';

import { cn } from '@shadcn/ui/lib/utils';

import { useAgent } from './hooks/use-agents';
import { useMemory, useMessages, useThreads } from './hooks/use-memory';
import { useAuth } from '../../providers/authentication-provider';
import { AndsendModel } from '@andsend/services-agent-shared';
import { useSidebars } from '@shadcn/ui/hooks/use-sidebars';
import dayjs from 'dayjs';

export const HAS_VISITED_AGENT_PAGE_KEY = 'hasVisitedAgentPage';
export const AgentPage = () => {
    const { threadId } = useParams();
    const [agentId, setAgentId] = useState<string>('andsendAgent');
    const [selectedModel, setSelectedModel] = useState<AndsendModel>(AndsendModel.GEMINI_2_0_FLASH);
    const { agent, mastra, isLoading: isAgentLoading, availableAgents } = useAgent(agentId, selectedModel);
    const { memory } = useMemory(agentId);
    const location = useLocation();
    const navigate = useNavigate();
    const forceClosedSidebar = useRef(false);
    const { messages, isLoading: isMessagesLoading } = useMessages({
        agentId: agentId!,
        threadId: threadId!,
        memory: !!memory?.result,
    });
    const {
        authState: { userId },
    } = useAuth();
    const {
        threads,
        isLoading: isThreadsLoading,
        mutate: refreshThreads,
    } = useThreads({ resourceid: userId!, agentId: agentId!, isMemoryEnabled: !!memory?.result });

    // Set localStorage flag when component mounts
    useEffect(() => {
        localStorage.setItem(HAS_VISITED_AGENT_PAGE_KEY, 'true');
    }, []);

    useEffect(() => {
        if (memory?.result && !threadId) {
            if (isThreadsLoading) {
                return;
            }

            let newThreadId = uuid();
            let existingThread = false;

            if (threads.length > 0) {
                const lastThread = threads?.sort(
                    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                )[0];
                // If last thread is created last 24 hours, use that thread
                if (lastThread.createdAt && dayjs(lastThread.createdAt).isAfter(dayjs().subtract(1, 'day'))) {
                    newThreadId = lastThread.id;
                    existingThread = true;
                }
            }

            // use @lukeed/uuid because we don't need a cryptographically secure uuid (this is a debugging local uuid)
            // using crypto.randomUUID() on a domain without https (ex a local domain like local.lan:4111) will cause a TypeError
            navigate(`/new/home/${newThreadId}`, {
                state: {
                    from: location.pathname,
                    existingThread,
                },
            });
        }
    }, [memory?.result, threadId, threads, isThreadsLoading]);

    const { toggleSidebar, getSidebarProps } = useSidebars();

    useEffect(() => {
        if (getSidebarProps('main').open && !forceClosedSidebar.current) {
            toggleSidebar('main');
            forceClosedSidebar.current = true;
        } else if (!getSidebarProps('main').open && !forceClosedSidebar.current) {
            forceClosedSidebar.current = true;
        }
        return () => {
            if (!getSidebarProps('main').open && forceClosedSidebar.current) {
                toggleSidebar('main');
                forceClosedSidebar.current = false;
            }
        };
    }, [toggleSidebar, getSidebarProps]);

    if (isAgentLoading) {
        return null;
    }
    if (isThreadsLoading) {
        return null;
    }

    return (
        <div
            className={cn(
                'flex flex-row w-full h-full p-4 [&_.aui-avatar-root]:w-12 [&_.aui-avatar-root]:h-12 [&_.aui-avatar-root>img]:scale-[0.70] [&_.aui-avatar-root>img]:object-contain [&_.aui-avatar-root]:bg-background-tertiary'
            )}
        >
            <Chat
                agentId={agentId!}
                agentName={agent?.name}
                threadId={threadId!}
                initialMessages={isMessagesLoading ? undefined : messages}
                memory={memory?.result}
                refreshThreadList={() => {
                    refreshThreads();
                }}
                availableAgents={availableAgents}
                setAgentId={setAgentId}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                mastra={mastra}
                threads={threads}
                isLoading={isThreadsLoading}
            />
        </div>
    );
};

export default AgentPage;
